import {
    inject,
    Injectable
} from "@angular/core";

import { v4 as generateUuid } from 'uuid';

import {
    ConfigurationManagerService,
    EntityManagerService,
    EnvironmentInfoService,
    OperatorManagerService,
    UserActiveSessionManagerService
} from "src/app/core";

import {
    SaleUtilsService
} from "src/app/features/cashregister";

import {
    ZPMSConnector
} from "./z-pms-connector.model";

import {
    Items,
    Sales,
    SalesDocuments,
    SalesPayments,
    Vat
} from "tilby-models";

import {
    keyBy,
    MathUtils
} from "src/app/shared/utils";

import {
    getPmsProductRef,
} from "../pms-utils";

import moment from "moment-timezone";

import {
    padCenter,
    upperFirst
} from "src/app/shared/string-utils";

import {
    BedzzleCommonService
} from "../bedzzle";

@Injectable({
    providedIn: 'root'
})
export class ZPMSConnectorUtilsService {
    private readonly bedzzleCommonService = inject(BedzzleCommonService)
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManager = inject(EntityManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly operatorManager = inject(OperatorManagerService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly userActiveSession = inject(UserActiveSessionManagerService);

    private async getRequiredResources() {
        const departments = await this.entityManager.departments.fetchCollectionOffline();
        const items = await this.entityManager.items.fetchCollectionOffline();

        return {
            departments: keyBy(departments, d => d.id),
            items: keyBy(items, i => i.id)
        };
    }

    private getItemUnit(item?: Items) {
        switch (upperFirst(item?.unit || '')) {
            case 'Pz': case 'Pezzo': case 'Pezzi': return 1;
            case 'Bottiglia': return 2;
            case 'Mazzo': return 3;
            case 'Sacco': return 4;
            case 'Lattina': return 5;
            case 'Grappolo': return 6;
            case 'Cassa': return 7;
            case 'Cad': case 'Cadauno': return 8;
            case 'Bicchiere': return 9;
            case 'Grammo': return 10;
            case 'G': case 'Grammi': return 11;
            case 'Chilo': return 12;
            case 'Kg': case 'Chili': return 13;
            case 'Pacco': return 14;
            case 'Paio': return 15;
            case 'ml': return 16;
            case 'Rotolo': return 18;
            case 'Porzione': return 19;
            case 'Totale': return 20;
            case 'N/A': default: return 17;
        }
    }

    public buildTail(folio: ZPMSConnector.FolioDto, saleData: Sales, providerID: string) {
        if (!folio) {
            return "";
        }

        const providerName = upperFirst(providerID);

        let tailLines = [];

        if (folio.keyDoorCode) {
            let headerFirstLine;
            let doorLabel;

            headerFirstLine = `* ADDEBITO IN CAMERA - ${providerName.toUpperCase()} *`;
            doorLabel = 'Camera';

            tailLines.push(padCenter(headerFirstLine, 46));

            const guest = folio.guestNames?.[0];

            if (guest) {
                if (guest.firstName && guest.lastName) {
                    tailLines.push(`Ospite: ${guest.firstName} ${guest.lastName}`);
                } else if (guest.companyName) {
                    tailLines.push(`Ospite: ${guest.companyName}`);
                }
            }

            tailLines.push(`${doorLabel}: ${folio.keyDoorCode}`);
        } else {
            tailLines.push(padCenter(`* ADDEBITO CONTO PASSANTE ${providerName.toUpperCase()} *`, 46));
        }

        tailLines.push(`Conto: ${folio.folioId}`);

        if (folio.checkIn && folio.checkOut) {
            tailLines.push(`Permanenza: ${moment(folio.checkIn, 'YYYY-MM-DD').format('L')}  - ${moment(folio.checkOut, 'YYYY-MM-DD').format('L')}`);
        }

        if (!this.configurationManagerService.getPreference(`pms_payments.hide_balance`)) {
            if (folio.currentBalance) {
                tailLines.push(`Saldo precedente: ${MathUtils.round(folio.currentBalance)} euro`);
            }

            tailLines.push(`Nuovo saldo: ${MathUtils.round((folio.currentBalance || 0) + saleData.final_amount!)} euro`);
        }

        return tailLines.join('\n');
    }

    private getDateString(date?: Date | string) {
        return moment(date).toISOString();
    }

    private getPaymentMethodCode(payment: SalesPayments): string {
        switch (payment.payment_method_type_id) {
            case 1: case 17: case 19: case 21: case 32: case 38: case 40:
                return '1';
            case 2: case 6: case 20: case 22: case 23: case 26: case 28: case 29: case 34: case 39: case 41: case 43:
                return '19';
            case 3:
                return '6';
            case 4:
                return '18';
            case 5:
                return '21';
            case 8:
                return '5';
            case 10: case 16: case 33:
                return '10';
            case 11: case 13: case 14: case 15: case 18: case 27: case 30: case 31: case 35: case 37: case 44: case 45: case 46:
                return '2';
            case 24: case 25:
                return '20';
            default:
                return '18';
        }
    }

    public async buildTransaction(folio: Partial<ZPMSConnector.FolioDto>, saleData: Sales): Promise<ZPMSConnector.SalesResourceDto> {
        const opData = this.operatorManager.getOperatorData();
        const resources = await this.getRequiredResources();

        const shop = this.userActiveSession.getSession()?.shop;
        const shopName = this.configurationManagerService.getPreference("general.shopname") || shop?.name || '';
        const documentUrl = !['stored'].includes(saleData.status) ? this.environmentInfoService.getPdfUrl(saleData.uuid!) : undefined;
        const isSplit = !!(saleData.final_amount! >= 0 && saleData.split_sale_origin_uuid);

        return {
            currency: saleData.currency,
            folioAlias: folio.folioAlias || saleData.notes,
            folioId: folio.folioId,
            folioRemark: "",
            items: saleData.sale_items!.map((item) => {
                // Vat/Nature
                const itemVat = resources.departments[item.department_id]?.vat || {} as Vat;
                const vatNature = itemVat.value
                    ? undefined
                    : itemVat.code || 'N4';

                // Quantities
                const refundMultiplier = item.quantity < 0 ? -1 : 1;

                const weight = item.original_quantity
                    ? !Number.isInteger(item.original_quantity) ? item.original_quantity : undefined
                    : !Number.isInteger(item.quantity) ? item.quantity : undefined;
                
                const intQuantity = weight === undefined
                    ? Math.floor(item.quantity) || 1
                    : 1;

                const decQuantity = (isSplit && weight === undefined)
                    ? (item.original_quantity === item.quantity
                        ? undefined
                        : item.quantity
                    )
                    : intQuantity

                return {
                    categoryAlias: item.category_name || `CAT${item.category_id || 0}`,
                    categoryId: item.category_id || 0,
                    categoryName: item.category_name || "Nessuna categoria",
                    completeNetUnitPrice: refundMultiplier * item.final_net_price,
                    completeUnitPrice: refundMultiplier * item.final_price,
                    groupAlias: item.department_name,
                    groupId: item.department_id,
                    groupName: item.department_name,
                    priceBeforeDiscount: refundMultiplier * this.saleUtilsService.getItemPartialUnitPrice(item),
                    productAlias: item.name || "",
                    ...getPmsProductRef(item),
                    productName: item.name || "",
                    productUnitId: this.getItemUnit(resources.items[item.item_id || ''] || {}),
                    quantity: intQuantity,
                    decQuantity: decQuantity,
                    weight: weight,
                    remark: item.notes,
                    sellingDate: this.getDateString(item.added_at),
                    vatCode: vatNature || itemVat.value.toString(),
                    vatDescription: item.department_name,
                    vatId: itemVat.id,
                    vatName: item.department_name,
                    vatNature: vatNature,
                    vatValue: itemVat.value,
                };
            }),
            orderId: saleData.uuid,
            originalTransactionId: saleData.split_sale_origin_uuid || saleData.uuid,
            orderName: saleData.id ? `${saleData.name} - ${saleData.id}`: saleData.name,
            outlet: {
                id: shop?.id || 0,
                alias: shop?.name,
                name: shopName,
                description: shopName
            },
            pmsUser: {
                firstName: opData.first_name,
                lastName: opData.last_name
            },
            stayId: folio.stayId,
            isSplit: isSplit,
            isLastDocument: !!(saleData.split_sale_origin_uuid && saleData.sale_parent_uuid === saleData.split_sale_origin_uuid),
            ticketImageUrl: documentUrl
        };
    }

    private getDocType(sale: Sales, isPaidSale: boolean, saleDocument?: SalesDocuments): ZPMSConnector.FiscalDocumentDto['docType'] {
        const isItalianNonFiscalDoc = (this.configurationManagerService.getShopCountry() === 'IT' && !!saleDocument?.document_type.startsWith('generic_'));

        if (sale.status === 'stored' || isItalianNonFiscalDoc) {
            return 'PR';
        }

        if (isPaidSale && saleDocument) {
            switch (saleDocument.document_type) {
                case 'invoice':
                case 'receipt_invoice':
                case 'summary_invoice':
                case 'shipping_invoice':
                case 'summary_e_rc':
                case 'summary_e_nrc':
                case 'generic_invoice':
                    return 'F';
                case 'e_invoice':
                    return sale.final_amount! < 0 ? 'NC' : 'F';
                case 'fiscal_receipt':
                case 'receipt':
                case 'commercial_doc':
                case 'generic_receipt':
                case 'generic_document':
                case 'credit_note':
                case 'refund_doc':
                case 'void_doc':
                    return 'R';
                default:
                    break;
            }
        }

        return 'R';
    }

    private getDocNum(sale: Sales, isPaidSale: boolean, saleDocument?: SalesDocuments) {
        if (!isPaidSale || !saleDocument) {
            return;
        }

        const docNumber = saleDocument.sequential_number || '';
        const docPrefix = saleDocument.sequential_number_prefix || '';

        return `${docNumber}${docPrefix ? `/${docPrefix.replace(/[^a-zA-Z0-9]/g, '')}` : ''}`;
    }

    public async buildExtTransaction(folio: Partial<ZPMSConnector.FolioDto>, saleData: Sales): Promise<ZPMSConnector.DocumentDto> {
        const {
            currency,
            folioRemark,
            items,
            ticketImageUrl,
            isLastDocument,
            isSplit,
            ...transaction
        } = await this.buildTransaction(folio, saleData);

        const isMagoEnabled = this.configurationManagerService.isMagoEnabled();
        const invoiceSendMode = this.configurationManagerService.getSetting('e_invoice.send_mode');
        const hasIntermediary = !!invoiceSendMode && !['disabled', 'download'].includes(invoiceSendMode);
        const isPaidSale = !saleData.payments?.some(p => p.payment_method_type_id == 8);

        // Extract profile and document
        const saleCustomer = saleData.sale_customer;
        const saleDocument = saleData.sale_documents?.find(sd => !['commercial_doc_signed', 'fiscal_provider', 'attachment'].includes(sd.document_type));

        // Group and calculate VAT from items
        const vatMap = new Map<string, ZPMSConnector.VatDto>();

        for (const item of items || []) {
            const vatIdKey = item.vatId?.toString() || '0';
            const vatValue = item.vatValue || 0;
            
            // Determine the correct quantity
            const qty = item.decQuantity ?? item.quantity ?? 1;

            const itemNetPrice = item.completeNetUnitPrice || 0;
            const itemGrossPrice = item.completeUnitPrice || 0;

            const rowNet = itemNetPrice * qty;
            const rowGross = itemGrossPrice * qty;
            const rowTax = rowGross - rowNet;

            const existing = vatMap.get(vatIdKey)!;

            if (existing) {
                existing.netAmount = MathUtils.round(existing.netAmount + rowNet);
                existing.taxAmount = MathUtils.round(existing.taxAmount + rowTax);
            } else {
                vatMap.set(vatIdKey, {
                    id: vatIdKey,
                    value: vatValue,
                    description: item.vatDescription || item.vatName || `IVA ${vatValue}%`,
                    nature: item.vatNature,
                    taxAmount: MathUtils.round(rowTax),
                    netAmount: MathUtils.round(rowNet)
                });
            }
        }

        return {
            ...transaction,
            createAtDate: this.getDateString(),
            document: {
                accounted: isMagoEnabled,
                amount: saleData.final_amount,
                cigPa: saleData.e_invoice?.purchase_order?.[0].cig_code,
                cupPa: saleData.e_invoice?.purchase_order?.[0].cup_code,
                currency: currency,
                docNumPa: saleData.e_invoice?.purchase_order?.[0].document_id,
                netAmount: saleData.final_net_amount,
                taxAmount: MathUtils.round(saleData.final_amount! - saleData.final_net_amount!),
                totalAmount: saleData.final_amount,
                totalNetAmount: saleData.final_net_amount,
                vat: Array.from(vatMap.values()),
                totalTaxAmount: MathUtils.round(saleData.final_amount! - saleData.final_net_amount!),
                docType: this.getDocType(saleData, isPaidSale, saleDocument),
                docEmitDate: this.bedzzleCommonService.getDocumentEmitDate(saleDocument?.date),
                docNum: this.getDocNum(saleData, isPaidSale, saleDocument),
                items: items,
                isPaid: isPaidSale,
                isSplit: isSplit,
                isLastDocument: isLastDocument,
                payments: saleData.payments?.map(payment => ({
                    id: generateUuid(),
                    code: this.getPaymentMethodCode(payment),
                    description: payment.payment_method_name,
                    methodId: this.bedzzleCommonService.getPaymentMethodId(payment),
                    datetime: this.getDateString(payment.date),
                    amount: payment.amount,
                })),
                sentToSDI: hasIntermediary,
                profile: saleCustomer ? {
                    id: saleCustomer.uuid!,
                    recipientCode: saleCustomer.sdi_code,
                    companyName: saleCustomer.company_name,
                    firstName: saleCustomer.first_name,
                    lastName: saleCustomer.last_name,
                    address: saleCustomer.billing_street,
                    zipCode: saleCustomer.billing_zip,
                    city: saleCustomer.billing_city,
                    province: saleCustomer.billing_prov,
                    country: saleCustomer.billing_country,
                    vatNumber: saleCustomer.vat_code,
                    taxCode: saleCustomer.tax_code,
                    sdiCode: saleCustomer.sdi_code,
                    pec: saleCustomer.email_pec
                } : undefined,
                ticketImageUrl: ticketImageUrl
            },
            originalOrderId: saleData.final_amount! < 0 ? saleData.sale_parent_uuid : undefined,
        };
    }
}